import type { AnalysisPage, ApplyProposalInput, AssistantAnswer, AssistantQuestion, AssistantState, ExtractionJob, ExtractionLot, LotInput, LotReport } from "@shared/modules/x-extraction";
import { api } from "../../../core/lib/api";

const BASE = import.meta.env.VITE_API_BASE_URL || "";

export interface EngineState {
  ollama: { baseUrl: string; model: string; available: boolean };
  autoCv: boolean;
  queue: Record<string, number>;
}

export function engineState(): Promise<EngineState> {
  return api<EngineState>("/api/extraction/etat");
}

export function listJobs(filter: { candidateId?: string; status?: string; lotId?: string } = {}): Promise<ExtractionJob[]> {
  const params = new URLSearchParams();
  if (filter.candidateId) params.set("candidateId", filter.candidateId);
  if (filter.status) params.set("status", filter.status);
  if (filter.lotId) params.set("lotId", filter.lotId);
  return api<ExtractionJob[]>(`/api/extraction/jobs?${params.toString()}`);
}

export function getJob(id: string): Promise<ExtractionJob & { anonymizedPreview?: string | null }> {
  return api(`/api/extraction/jobs/${id}?preview=1`);
}

export function applyJob(id: string, input: ApplyProposalInput): Promise<ExtractionJob> {
  return api<ExtractionJob>(`/api/extraction/jobs/${id}/appliquer`, { method: "POST", body: JSON.stringify(input) });
}

export function rejectJob(id: string): Promise<ExtractionJob> {
  return api<ExtractionJob>(`/api/extraction/jobs/${id}/rejeter`, { method: "POST" });
}

export function requeueJob(id: string): Promise<ExtractionJob> {
  return api<ExtractionJob>(`/api/extraction/jobs/${id}/relancer`, { method: "POST" });
}

/** Importation de CV sans dossier : point d'entrée du module x-documents (appel d'API seulement). */
export async function importCvs(files: File[]): Promise<{ created: { id: string }[]; rejected: { name: string; reason: string }[] }> {
  const form = new FormData();
  for (const f of files) form.append("fichiers", f, f.name);
  const res = await fetch(`${BASE}/api/documents/importations`, { method: "POST", body: form, credentials: "include" });
  const body = (await res.json().catch(() => ({ created: [], rejected: [] }))) as { created: { id: string }[]; rejected: { name: string; reason: string }[] };
  if (!res.ok && !body.created?.length) throw new Error("importation refusée");
  return body;
}

export function listLots(archives = false): Promise<ExtractionLot[]> {
  return api<ExtractionLot[]>(`/api/extraction/lots${archives ? "?archives=1" : ""}`);
}

export function createLot(input: LotInput): Promise<ExtractionLot> {
  return api<ExtractionLot>("/api/extraction/lots", { method: "POST", body: JSON.stringify(input) });
}

export function updateLot(id: string, input: Partial<LotInput> & { archived?: boolean }): Promise<ExtractionLot> {
  return api<ExtractionLot>(`/api/extraction/lots/${id}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function getLotReport(id: string): Promise<LotReport> {
  return api<LotReport>(`/api/extraction/lots/${id}`);
}

export function attachToLot(id: string, documentIds: string[]): Promise<{ jobs: ExtractionJob[]; missing: number }> {
  return api(`/api/extraction/lots/${id}/documents`, { method: "POST", body: JSON.stringify({ documentIds }) });
}

export function lotExportUrl(id: string): string {
  return `${BASE}/api/extraction/lots/${id}/rapport.xlsx`;
}

export function listAnalyses(filter: Record<string, string | number>): Promise<AnalysisPage> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) if (v !== "" && v !== undefined) params.set(k, String(v));
  return api<AnalysisPage>(`/api/extraction/analyses?${params.toString()}`);
}

/** Téléchargement du CV d'origine : point d'entrée du module x-documents (lien seulement). */
export function documentUrl(documentId: string, inline = false): string {
  return `${BASE}/api/documents/${documentId}/telecharger${inline ? "?inline=1" : ""}`;
}

export function assistantState(): Promise<AssistantState> {
  return api<AssistantState>("/api/extraction/assistant/etat");
}

export function askAssistant(input: AssistantQuestion): Promise<AssistantAnswer> {
  return api<AssistantAnswer>("/api/extraction/assistant", { method: "POST", body: JSON.stringify(input) });
}
